import {
  getPasswordSessionGeneration,
  isPasswordSessionTransitionActive,
  waitForPasswordSessionTransition,
} from "./password-session-transition";
import { useQuery } from "@tanstack/react-query";
import { authClient } from "@/lib/authClient";
import { queryClient } from "@/lib/queryClient";
import type { QueryClient } from "@tanstack/react-query";
import { useAuthUiStore } from "./store";
import { clearPendingInvitation } from "@/features/invitations/pendingInvitation";
import { useEmailDrafts } from "@/features/admin-email-templates/drafts";
import { analytics } from "@/features/analytics/service";
import { clearOAuthQuery } from "@/features/oauth/request";

export function appSessionOptions() {
  return {
    queryKey: ["session"],
    queryFn: async () => {
      // Reads started before rotation can finish after it. Observe a generation,
      // not only the active flag, so an old null response never replaces the
      // confirmed new session after the transition has already finished.
      for (;;) {
        await waitForPasswordSessionTransition();
        const generation = getPasswordSessionGeneration();
        try {
          const result = await authClient.getSession({
            query: { disableCookieCache: true },
          });
          if (
            generation !== getPasswordSessionGeneration() ||
            isPasswordSessionTransitionActive()
          )
            continue;
          if (result.error)
            throw new Error(
              result.error.message ?? "Could not check your session.",
            );
          if (result.data?.user.id && useEmailDrafts.getState().actorId && useEmailDrafts.getState().actorId !== result.data.user.id) useEmailDrafts.getState().clear();
          return result.data;
        } catch (error) {
          if (
            generation !== getPasswordSessionGeneration() ||
            isPasswordSessionTransitionActive()
          )
            continue;
          throw error;
        }
      }
    },
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  };
}

export function useAppSession(
  options: { refetchInterval?: number; refetchOnMount?: boolean } = {},
) {
  return useQuery({ ...appSessionOptions(), ...options });
}

export async function finishSignOut(
  result: { error: { message?: string } | null },
  cache: QueryClient = queryClient,
) {
  if (result.error)
    throw new Error(
      result.error.message ?? "Could not sign out. Please try again.",
    );
  await cache.cancelQueries();
  cache.clear();
  useEmailDrafts.getState().clear();
  useAuthUiStore.getState().finishSocialSignIn();
}

export async function signOutOfWebApp() {
  analytics.clear();
  await finishSignOut(await authClient.signOut());
  clearPendingInvitation();
  clearOAuthQuery();
  window.location.assign("/auth/sign-in");
}

export async function switchAccount(returnTo: string) {
  analytics.clear();
  await finishSignOut(await authClient.signOut());
  window.location.assign(
    `/auth/sign-in?${new URLSearchParams({ next: returnTo })}`,
  );
}
